import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Card, CardContent, Grid, Tooltip, Typography } from "@mui/material";
import { FaCheck, FaPlus, FaTrash, FaTrashAlt } from "react-icons/fa";
import DropdownField from "../core/DropdownField";
import { displayNotification, generateOptionsFromData, sendAPIRequest } from "helpers";
import { useGetListByMasterIdQuery } from "modules/pvs_activities/redux/service/lists";
import { useSelector } from "react-redux";
import AutoCompleteDropdown from "components/autocomplete/AutoCompleteDropdown";
import DataTable from "pages/components/DataTable";
import { useTranslation } from "react-i18next";
import { Modal } from "react-bootstrap";
import Row from "components/common/Row";
import Column from "components/common/Column";

const initialForm = {
    team_leader: "",
};

const initialFilters = {
    country_id: [],
    lang_id: [],
    expertise: [],
    expert_name: [],
    mission_country_id: [],
    country_of_residence: [],
};

const allowedPositions = [87, 90, 91, 92, 94];


const AddMissionExpertsModalUpdated = ({ isShow, initModal }) => {
    const { t } = useTranslation();
    const authData = useSelector((s) => s.auth.data);
    const [isLoading, setIsLoading] = useState(false);
    const [experts, setExperts] = useState([]);
    const [data, setData] = useState({});
    const [filters, setFilters] = useState({ ...initialFilters });
    const [isSaving, setIsSaving] = useState(false);
    const [rowSelectionModel, setRowSelectionModel] = useState([]);
    const [expertise, setExpertise] = useState([
        { id: "pvs-expert", name: "PVS Expert" },
        { id: "aqua-expert", name: "Aqua Expert" },
        { id: "gap-expert", name: "GAP Expert" },
        { id: "legislation-expert", name: "Legislation Expert" },
        { id: "laboratory-expert", name: "Laboratory Expert" },
    ]);
    const [country, setCountry] = useState([]);
    const languagesRes = useGetListByMasterIdQuery({ id: "3" });
    // const positionsRes = useGetListByMasterIdQuery({ id: "17", params: { only_ids: allowedPositions } });
    const positionsRes = useGetListByMasterIdQuery({ id: "17", params: { except_ids: '94' } });

    useEffect(() => {
        loadCountries();
        loadExperts();
    }, []);
    // const handleSave = async () => {
    //     try {
    //         if (!window.confirm("You are about to save data. Are you sure?")) {
    //             return;
    //         }
    //         if (Object.values(data).filter((v) => v === "").length > 0) {
    //             displayNotification("warning", t("-1", "Please select position for each selected expert"));
    //             return;
    //         }
    //         if (formDataContext.mode === "preview") {
    //             displayNotification("success", `${t("-1", "Successfully saved experts")} [B]`);
    //             return;
    //         }
    //         setIsSaving(true);
    //         const req = await sendAPIRequest(
    //             `/api/pvsa/dform/save-experts-from-movement/${formDataContext.movementId}`,
    //             "POST",
    //             {
    //                 entries: data,
    //             }
    //         );
    //         const res = await req.json();
    //         if (res.success) {
    //             dispatch(dFormApi.util.invalidateTags(['ExpertsFromMovement']));
    //             displayNotification("success", `${t("-1", "Successfully saved experts")}`);
    //             modalRef.current?.closeModal();
    //         } else {
    //             displayNotification("error", `${t("-1", "Error saving experts")} [B]`);
    //         }
    //         setIsSaving(false);
    //     } catch (e) {
    //         setIsSaving(false);
    //         console.error(e);
    //         displayNotification("error", `${t("-1", "Error saving experts")} [F]`);
    //     }
    // };
    const loadCountries = async () => {
        try {
            const req = await sendAPIRequest(`/api/country`);
            const res = await req.json();
            if (res.success) {
                setCountry(res.data);
            }
        } catch (e) {
            console.log(e);
        }
    };

    const loadExperts = async (chk = true) => {
        try {
            setIsLoading(true);
            let fltr = { ...filters };
            if (chk == false) {
                fltr = initialFilters;
            }
            let params = new URLSearchParams();
            fltr.country_id.forEach((element) => {
                params.append("country_id", element);
            });
            fltr.country_of_residence.forEach((element) => {
                params.append("country_of_residence", element);
            });
            fltr.lang_id.forEach((element) => {
                params.append("lang_id", element);
            });
            fltr.expertise.forEach((element) => {
                params.append("expertise", element);
            });
            fltr.expert_name.forEach((element) => {
                params.append("person_id", element);
            });
            fltr.mission_country_id.forEach((element) => {
                params.append("mission_country_id", element);
            });
            if (authData && authData.id) {
                params.append("user_id", authData.id);
            }
            const req = await sendAPIRequest(`/api/expert/load_experts?${params}`);
            const res = await req.json();
            if (res.success) {
                setExperts(res.data);
                setIsLoading(false);
            } else {
                displayNotification("error", `${t("-1", "Error loading experts data")} [B]`);
            }
        } catch (e) {
            console.error(e);
            displayNotification("error", `${t("-1", "Error loading experts data")} [F]`);
        }
    };

    const countryOptions = useMemo(() => {
        return generateOptionsFromData(country, {
            value: "id",
            label: "country_complete_name",
        });
    }, [country]);

    const languageOptions = useMemo(() => {
        if (languagesRes.isLoading)
            return [
                {
                    value: "",
                    label: "Loading...",
                },
            ];
        if (languagesRes.isError)
            return [
                {
                    value: "",
                    label: "Error Loading...",
                },
            ];
        return generateOptionsFromData(languagesRes.data, {
            value: "id",
            label: "list_element_name",
        });
    }, [languagesRes]);

    const expertiseOptions = useMemo(() => {
        return generateOptionsFromData(expertise, {
            value: "id",
            label: "name",
        });
    }, [expertise]);

    const finalisedExperts = useMemo(() => {
        return Object.values(data).filter((r) => r.is_locked === true && r.position_id !== "");
    }, [data]);

    const positions = useMemo(() => {
        if (positionsRes.isSuccess) {
            return positionsRes.data;
        }
        return [];
    }, [positionsRes]);

    const positionsOptions = useMemo(() => {
        if (positionsRes.isLoading)
            return [
                {
                    value: "",
                    label: "Loading...",
                },
            ];
        if (positionsRes.isError)
            return [
                {
                    value: "",
                    label: "Error Loading...",
                },
            ];
        return generateOptionsFromData(positionsRes.data, {
            value: "id",
            label: "list_element_name",
        });
    }, [positionsRes]);


    //     (newRowSelectionModel) => {
    //         const tmp = { ...data };
    //         // Add new row selection
    //         for (const expId of newRowSelectionModel) {
    //             if (tmp[expId] === undefined) {
    //                 tmp[expId] = {
    //                     expert_id: expId,
    //                     position_id: "",
    //                     is_locked: false,
    //                 };
    //             }
    //         }
    //         // If no rows are selected, filter unlocked rows
    //         if (newRowSelectionModel.length === 0) {
    //             const filteredData = Object.fromEntries(
    //                 Object.entries(tmp).filter(([key, value]) => value.is_locked === false)
    //             );
    //             setData(filteredData);
    //         } else {
    //             setData(tmp);
    //         }
    //         setRowSelectionModel([...newRowSelectionModel]);
    //     },
    //     [data]
    // );

    const handleRowSelection = useCallback(
        (newRowSelectionModel) => {
            const tmp = { ...data };
            for (const expId of newRowSelectionModel) {
                if (tmp[expId] === undefined) {
                    tmp[expId] = "";
                }
            }
            setData(tmp);
            setRowSelectionModel([...newRowSelectionModel]);
        },
        [data]
    );
    const resetFilter = () => {
        setFilters({ ...initialFilters });
        loadExperts(false);
    };

    const getExpertObj = useCallback(
        (positionId) => {
            return {};
        },
        [experts, data]
    );

    const navigationFunction = (key, value) => {
        let params = { ...filters };
        params[key] = value;
        setFilters({
            ...params,
        });
    };
    return (
        <Modal show={isShow} animation={false} size="xl" className="size_Modal">
            <Modal.Header closeButton onClick={() => {
                initModal();
            }}>
                <Modal.Title><h1>Add New Mission Expert Set</h1></Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="">
                    <Row>
                        <Column col={12} >
                            <>
                                <div className="border border-1">
                                    <Box sx={{ display: "flex", paddingLeft: '2px' }}>
                                        <Typography variant="h5" gutterBottom>
                                            {t("14", "Filters")}
                                        </Typography>

                                    </Box>
                                    <Box sx={{ px: 2, py: 1 }} className="mb-2">
                                        <div className="row">
                                            <div className="col-md-4 col-12">
                                                <Tooltip title={t("667", "You can select multiple options")} placement="top" arrow>
                                                    <>
                                                        <AutoCompleteDropdown
                                                            error={false}
                                                            required={false}
                                                            label={t("2917", "Nationality")}
                                                            multiple={true}
                                                            hasselectall={true}
                                                            options={countryOptions}
                                                            onChange={(event, value) => {
                                                                if (value.some((v) => v.value == "Select all")) {
                                                                    if (filters.country_id.length == countryOptions.length) {
                                                                        // Deselect all options
                                                                        navigationFunction("country_id", []);
                                                                    } else {
                                                                        // Select all options
                                                                        navigationFunction(
                                                                            "country_id",
                                                                            countryOptions.map((r) => r.value)
                                                                        );
                                                                    }
                                                                } else {
                                                                    navigationFunction(
                                                                        "country_id",
                                                                        value.map((r) => r.value)
                                                                    );
                                                                }
                                                            }}
                                                            value={filters.country_id}
                                                        />
                                                    </>
                                                </Tooltip>
                                            </div>
                                            <div className="col-md-4 col-12">
                                                <Tooltip title={t("667", "You can select multiple options")} placement="top" arrow>
                                                    <>
                                                        <AutoCompleteDropdown
                                                            error={false}
                                                            required={false}
                                                            label={t("2887", "Country Missions")}
                                                            multiple={true}
                                                            hasselectall={true}
                                                            options={countryOptions}
                                                            onChange={(event, value) => {
                                                                if (value.some((v) => v.value == "Select all")) {
                                                                    if (
                                                                        filters.mission_country_id.length == countryOptions.length
                                                                    ) {
                                                                        // Deselect all options
                                                                        navigationFunction("mission_country_id", []);
                                                                    } else {
                                                                        // Select all options
                                                                        navigationFunction(
                                                                            "mission_country_id",
                                                                            countryOptions.map((r) => r.value)
                                                                        );
                                                                    }
                                                                } else {
                                                                    navigationFunction(
                                                                        "mission_country_id",
                                                                        value.map((r) => r.value)
                                                                    );
                                                                }
                                                            }}
                                                            value={filters.mission_country_id}
                                                        />
                                                    </>
                                                </Tooltip>
                                            </div>
                                            <div className="col-md-4 col-12">
                                                <Tooltip title={t("667", "You can select multiple options")} placement="top" arrow>
                                                    <>
                                                        <DropdownField
                                                            label={t("19", "All Languages")}
                                                            hasOptions={true}
                                                            options={languageOptions}
                                                            plain={true}
                                                            size={"small"}
                                                            multiple={true}
                                                            selectAll={true}
                                                            value={filters.lang_id ? filters.lang_id : []}
                                                            onChange={(value) => navigationFunction("lang_id", value)}
                                                        />
                                                    </>
                                                </Tooltip>
                                            </div>
                                            <div className="col-md-4 col-12">
                                                <Tooltip title={t("667", "You can select multiple options")} placement="top" arrow>
                                                    <>
                                                        <AutoCompleteDropdown
                                                            error={false}
                                                            required={false}
                                                            label={t("191", "All Expertise")}
                                                            multiple={true}
                                                            hasselectall={true}
                                                            options={expertiseOptions}
                                                            onChange={(event, value) => {
                                                                if (value.some((v) => v.value == "Select all")) {
                                                                    if (filters.expertise.length == expertiseOptions.length) {
                                                                        // Deselect all options
                                                                        navigationFunction("expertise", []);
                                                                    } else {
                                                                        // Select all options
                                                                        navigationFunction(
                                                                            "expertise",
                                                                            expertiseOptions.map((r) => r.value)
                                                                        );
                                                                    }
                                                                } else {
                                                                    navigationFunction(
                                                                        "expertise",
                                                                        value.map((r) => r.value)
                                                                    );
                                                                }
                                                            }}
                                                            value={filters.expertise}
                                                        />
                                                    </>
                                                </Tooltip>
                                            </div></div>





                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: 'end', justifyContent: 'end' }}>

                                        <Box sx={{ ml: "auto" }}>
                                            <button
                                                className="btn btn--outline btn-sm fs-7 lh-1 mb-2 ms-2"
                                                type="button"
                                                onClick={() => resetFilter()}
                                                style={{ minWidth: "120px", minHeight: "30px" }}
                                            >
                                                <span className="align-middle">{t("258", "Reset")}</span>
                                                <i className="fas fa-arrow-rotate-left text-black ms-2" />
                                            </button>
                                            <button
                                                className="btn btn--primary btn-sm fs-7 lh-0 mb-2 ms-1"
                                                type="button"
                                                onClick={() => loadExperts()}
                                                style={{ minWidth: "120px", minHeight: "30px" }}
                                            >
                                                <span className="align-middle">{t("21", "Search")}</span>
                                                <i className="fas fa-search text-white ms-2 " />
                                            </button>
                                        </Box>
                                    </Box>
                                </div>
                                <DataTable
                                    // disabled={isSaving}
                                    pageSize={5}
                                    checkboxSelection={true}
                                    onRowSelectionModelChange={handleRowSelection}
                                    rowSelectionModel={rowSelectionModel}
                                    // getRowId={(row) => `experts-${Math.random().toString(36).substr(2, 9)}-${row.id}`}
                                    getRowId={(row) => row.id ?? ""}
                                    // isRowSelectable={(params) => {
                                    //     return finalisedExperts.findIndex((r) => r.expert_id === params.row.id) !== -1 ||
                                    //         finalisedExperts.length === allowedPositions.length
                                    //         ? false
                                    //         : rowSelectionModel.length === 0 || rowSelectionModel.includes(params.row.id);
                                    // }}
                                    isRowSelectable={(params) => {
                                        return (
                                            finalisedExperts.findIndex((r) => r.expert_id === params.row.id) === -1
                                        );
                                    }}
                                    isLoading={isLoading}
                                    rows={experts}
                                    className="text-white bg-primary rounded-0"
                                    columns={[
                                        {
                                            field: "first_name",
                                            headerName: t("192", "First Name"),
                                            headerClassName: "text-white bg-primary rounded-0",
                                            minWidth: 100,
                                            flex: 1,
                                        },
                                        {
                                            field: "last_name",
                                            headerName: t("193", "Last Name"),
                                            headerClassName: "text-white bg-primary rounded-0",
                                            minWidth: 100,
                                            flex: 1,
                                        },
                                        {
                                            field: "country_name",
                                            headerName: t("2917", "Nationality"),
                                            headerClassName: "text-white bg-primary rounded-0",
                                            flex: 1,
                                        },
                                        {
                                            field: "language",
                                            headerName: t("26", "Language"),
                                            headerClassName: "text-white bg-primary rounded-0",
                                            flex: 1,
                                            valueGetter: (params) => params.row.languages.map((i) => i.name).join(","),
                                        },
                                        {
                                            field: "expert_areas",
                                            headerName: t("40", "Expertise"),
                                            headerClassName: "text-white bg-primary rounded-0",
                                            flex: 1,
                                            renderCell: (params) => (
                                                <div style={{ maxHeight: "100px", overflowY: "auto" }}>
                                                    {params.row.expert_areas.map((area, index) => (
                                                        <div className="col-6 mb-1" key={index}>
                                                            <span
                                                                className={`badge rounded-3 p-2 ${area === "PVS Expert" && "bg-green"
                                                                    } ${area === "Aqua Expert" && "bg-light-blue"} ${area === "Gap Expert" && "bg-light-orange"
                                                                    } ${area === "Legislation Expert" && "bg-light-alice-blue"} ${area === "Laboratory Expert" && "bg-light-gray"
                                                                    }`}
                                                                style={{ fontSize: "0.875rem" }}
                                                            >
                                                                <div key={index}>{area}</div>
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ),
                                        },
                                    ]}
                                    sx={{
                                        "& .MuiDataGrid-columnHeaders .MuiCheckbox-root": {
                                            display: "none",
                                        },
                                    }}
                                />
                            </>
                        </Column>
                        <Column col={12}>
                            {finalisedExperts.length > 0 || rowSelectionModel.length > 0 ? (
                                <>
                                    {finalisedExperts.map((r) => {
                                        const expert = experts.find((s) => String(s.id) === String(r.expert_id));
                                        const position = positions.find((s) => s.id === r.position_id);
                                        return (
                                            <div className="bg-light-2 p-4 mb-3" key={r.expert_id}>
                                                <div className="mb-2">
                                                    <strong className="me-2">Name:</strong>
                                                    <span>
                                                        {expert?.first_name} {expert?.last_name}
                                                    </span>
                                                </div>
                                                <div className="mb-2">
                                                    <strong className="me-2">Position:</strong>
                                                    <span>{position?.list_element_name ?? "-"}</span>
                                                </div>
                                                <div className="mt-2 d-flex">
                                                    <div className="ms-auto">
                                                        <Button
                                                            variant="contained"
                                                            color="error"
                                                            onClick={() => {
                                                                const tmp = { ...data };
                                                                delete tmp[r.expert_id];
                                                                setData(tmp);
                                                            }}
                                                        >
                                                            <FaTrashAlt />
                                                            <span className="ms-1">Delete</span>
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}


                                    {rowSelectionModel.length > 0 ? (
                                        <Box className="bg-light-3 p-4" mb={2}>
                                            <ol>
                                                {rowSelectionModel.map((r) => {
                                                    const expert = experts.find((s) => String(s.id) === String(r));
                                                    return (
                                                        <li key={expert.id}>
                                                            <div className="row mb-2">
                                                                <div className="col-md-6 col-sm-6 col-12 d-flex">
                                                                    <div className="my-auto">
                                                                        {expert?.first_name} {expert?.last_name}
                                                                    </div>
                                                                </div>
                                                                <div className="col-md-6 col-sm-6 col-12">
                                                                    <DropdownField
                                                                        label={"Select Position"}
                                                                        value={data[r]}
                                                                        onChange={(val) => setData({ ...data, [r]: val })}
                                                                        hasOptions={true}
                                                                        options={positionsOptions}
                                                                        disabled={false}
                                                                        plain={true}
                                                                        size={"small"}
                                                                        className="bg-white"
                                                                    />
                                                                </div>
                                                            </div>
                                                        </li>
                                                    );
                                                })}
                                            </ol>
                                            <div className="d-flex">
                                                <div className="ms-auto">
                                                    <Button variant="contained" color="success">
                                                        <FaCheck />
                                                        <span className="ms-1">{isSaving ? "Saving..." : "Save"}</span>
                                                    </Button>
                                                </div>
                                            </div>
                                        </Box>
                                    ) : (
                                        <Box mb={2}>
                                            <Alert severity="info">
                                                No Experts selected. Please select to continue
                                            </Alert>
                                        </Box>
                                    )}
                                </>
                            ) : (
                                // <Alert severity="info" className="my-3">
                                //     No Experts selected. Please select to continue
                                // </Alert>
                                <></>
                            )}

                        </Column>


                    </Row>
                </div>

            </Modal.Body>
        </Modal>
    )
}

export default AddMissionExpertsModalUpdated
